/**
 * Wrap Omise.createToken (callback API) in a proper Promise.
 *
 * Bug in legacy code:
 *   The old createToken callback set a modal on error but never called
 *   reject() — so callers awaiting the Promise either hung forever or
 *   resolved with `undefined`, leading to silent failures (token=undefined
 *   sent to backend → cryptic 500).
 *
 * This module fixes that by:
 *  - Always resolving on statusCode === 200 with the token id
 *  - Always rejecting on any other status with a normalized error
 *  - Rejecting if Omise SDK is missing entirely
 *
 * Why a module function (not a React hook):
 *  No React state — it's a one-shot Promise. Hooks add overhead with
 *  zero benefit here. The orchestrator's useSubmitSubscription calls this
 *  directly inside its mutation function.
 *
 * Mock mode:
 *  Returns a fake token so the flow can be exercised end-to-end in dev
 *  without real Omise keys.
 *
 * Public key strategy:
 *  Omise public_key MUST come from the backend (per project) — never from
 *  env vars on the frontend, since each project has its own Omise account.
 *  Caller passes it in.
 */
// const USE_MOCK = true;
const USE_MOCK = false;

/**
 * @param {Object} cardData - { card_number, card_holder, expiry_month, expiry_year, cvv }
 * @param {string} publicKey - Omise public key (pkey_test_... or pkey_...)
 * @returns {Promise<string>} Omise token id (e.g. "tokn_test_xxxx")
 */
export async function tokenizeCard(cardData, publicKey) {
  console.log("🔍 tokenizeCard cardData:", cardData);
  console.log("🔍 tokenizeCard publicKey:", publicKey);
  console.log(
    "🔍 cardData.expiry_month:",
    cardData?.expiry_month,
    typeof cardData?.expiry_month
  );
  console.log("🔍 cardData.expiry_year:", cardData?.expiry_year);
  console.log("🔍 cardData.card_number:", cardData?.card_number?.length);

  if (USE_MOCK) {
    // Simulate Omise round-trip
    await new Promise((r) => setTimeout(r, 600));
    return `tokn_test_mock_${Date.now()}`;
  }

  if (typeof window === "undefined" || !window.Omise) {
    throw normalizeError({
      code: "OMISE_SDK_MISSING",
      message: "ระบบชำระเงินยังไม่พร้อมใช้งาน กรุณารีเฟรชหน้าและลองใหม่",
    });
  }

  if (!publicKey) {
    throw normalizeError({
      code: "OMISE_KEY_MISSING",

      message: "ระบบชำระเงินยังไม่ได้ตั้งค่า",
    });
  }

  window.Omise.setPublicKey(publicKey);

  return new Promise((resolve, reject) => {
    const payload = {
      name: cardData.card_holder,
      number: cardData.card_number,
      expiration_month: Number(cardData.expiry_month),
      expiration_year: Number(cardData.expiry_year),
      security_code: cardData.cvv,
    };

    window.Omise.createToken("card", payload, (statusCode, response) => {
      if (statusCode === 200 && response?.id) {
        resolve(response.id);
        return;
      }
      reject(
        normalizeError({
          code: response?.code || "TOKENIZATION_FAILED",
          message:
            response?.message ||
            "ไม่สามารถตรวจสอบบัตรได้ กรุณาตรวจสอบข้อมูลบัตร",
          status: statusCode,
        })
      );
    });
  });
}

// Same shape that subscriptionClient interceptor produces, so ErrorModal can
// handle Omise errors and backend errors uniformly.
function normalizeError({ status = null, code, message }) {
  const err = new Error(message);
  err.status = status;
  err.code = code;
  err.message = message;
  return err;
}
